// import { SharingLead } from '@prisma/client'
import Button from 'components/Button'
import TwoCirclesSpinner from 'components/Spinner/TwoCirclesSpinner'
import { createSharingLead, CreateSharingLead, CreateSharingLeadField } from 'data/sharing/lead/client'
import { usePlausible } from 'next-plausible'
import { Dispatch, FormEvent, MouseEvent as ReactMouseEvent, SetStateAction, useState } from 'react'
import { isEmailAddress } from 'utilities/text'
import { isUrl } from 'utilities/url'
import styles from './LeadForm.module.css'

type Props = {
  idSuffix?: string,
  formFields?: FormField[],
  analytics?: {
    key?: string,
    metadata?: Record<string, string>
  },
  onSubmit?: (data: CreateSharingLead, setStatus: Dispatch<SetStateAction<Status>>) => unknown,
  submitButtonText?: string,
}

type Status = 'sending' | 'error' | 'success' | null

type FormField = {
  id: CreateSharingLeadField,
  label: string,
  placeholder?: string,
  fieldType: string,
  inputType: string,
  validateOnSubmit?: (value: string) => boolean,
  errorMessage?: string,
}

const defaultFormFields: FormField[] = [{
  id: 'url',
  label: 'Your Blog’s URL',
  placeholder: 'https://myblog.example.com',
  fieldType: 'text',
  inputType: 'email',
  validateOnSubmit: isUrl,
  errorMessage: 'Please enter a valid URL for your blog',
}, {
  id: 'emailAddress',
  label: 'Your Email Address',
  placeholder: 'blogger@example.com',
  fieldType: 'text',
  inputType: 'email',
  validateOnSubmit: isEmailAddress,
  errorMessage: 'Please enter a valid email address',
}]

const defaultOnSubmit = async (lead: CreateSharingLead, setStatus: Dispatch<SetStateAction<Status>>) => {
  console.log('SUBMITTED', lead)
  const result = await createSharingLead(lead)
  if (result) {
    setStatus('success')
  } else {
    setStatus('error')
  }
}

const LeadForm = ({
  idSuffix = '',
  formFields = defaultFormFields,
  analytics = {},
  onSubmit = defaultOnSubmit,
  submitButtonText = 'Submit'
}: Props) => {
  const [status, setStatus] = useState<Status>(null)
  const [fieldData, setFieldData] = useState<{ [key: string]: string }>({})
  const [localErrorMessage, setLocalErrorMessage] = useState('')
  const plausible = usePlausible()
  const handleInputChange = (fieldId: string, newValue: string) => {
    setLocalErrorMessage('')
    setFieldData({
      ...fieldData,
      [fieldId]: newValue,
    })
  }
  const handleSubmit = (e?: FormEvent<HTMLFormElement> | ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.preventDefault()
    const hasErrors = formFields.reduce<string>((previous, current: FormField) => {
      const value = fieldData[current.id] || ''
      const fieldError = !!current?.validateOnSubmit
        ? current.validateOnSubmit(value) ? '' : current.errorMessage
        : ''
      console.log({ previous, fieldError })
      return fieldError
        ? previous
          ? `${previous}\r\n${fieldError}`
          : fieldError
        : previous
    }, '')
    if (hasErrors) {
      console.log('SET LOCAL ERROR MESSAGES', hasErrors)
      setLocalErrorMessage(hasErrors)
    } else {
      analytics.key && plausible(analytics.key, { props: analytics.metadata })
      const dataToSubmit = formFields.reduce<Record<string, string>>((previous, current: FormField) => ({
        ...previous,
        [current.id]: fieldData[current.id] || '',
      }), {})
      onSubmit(dataToSubmit, setStatus)
    }
  }
  const isStatus = (statusCheck: Status) => Boolean(status === statusCheck)
  return <div className={styles.leadForm}>
    <div className={styles.statusContainer}>
      {
        isStatus('sending')
          && <div className={styles.statusSending}>
            <TwoCirclesSpinner scale={0.5} color='#000' />
            <span>Sending...</span>
          </div>
      }
      {/* {
        isStatus('error')
          && <div
            className={styles.statusError}
            dangerouslySetInnerHTML={{ __html: message as string }}
          />
      } */}
      {
        localErrorMessage
          && <div
            className={styles.statusError}
            dangerouslySetInnerHTML={{ __html: localErrorMessage as string }}
          />
      }
      {
        isStatus('success')
          && <div
            className='py-8 px-8 rounded-md bg-emerald-100'
            >
            <h1 className='text-2xl mb-3'>Check your email</h1>
            <p>You’re nearly done! Your instructions for the 7-second final step will arrive in your inbox momentarily.</p>
          </div>
      }
    </div>
    {

      !isStatus('success')
      && <form onSubmit={handleSubmit} className={styles.formFields}>
          <div className={styles.fields}>
            { formFields.map((field) => <div key={field.id} className={styles.field}>
              <label className={styles.fieldLabel} htmlFor={`lead-${field.id}${idSuffix}`}>
                { field.label }
              </label>
              <input
                id={`lead-${field.id}${idSuffix}`}
                type={field.inputType}
                className='block w-full border border-gray-300 rounded-md px-5 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:border-rose-500 focus:ring-rose-500'
                placeholder={field.placeholder}
                value={fieldData[field.id] || ''}
                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                  const newValue = ev?.target?.value
                  handleInputChange(field.id, newValue)
                }}
              />
            </div>)}
          </div>
          <div className='mt-4 sm:mt-0 sm:ml-3'>
            <Button
              type='submit'
              onClick={handleSubmit}
              className='!bg-rose-500 hover:bg-rose-600 focus:ring-rose-500 mx-3 md:mx-0'
              // className='font-[Montserrat] block w-full md:w-auto rounded-md border border-transparent px-5 py-3 bg-rose-500 text-base font-medium text-white shadow hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 sm:px-10'
            >
              { submitButtonText }
            </Button>
          </div>
        </form>
    }
  </div>
}


export default LeadForm
